import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { ensureCsrfToken } from "../../services/authService";
import { loadOffice, messageParent } from "./officeBridge";

/*
 * Opened by the Word panel in an Office dialog (a real top-level window on
 * our own domain, so WHG Docs' normal sign-in cookies apply). It asks the
 * API for a bearer token for the signed-in person and hands it back to the
 * panel. If nobody is signed in, it sends them through the usual sign-in and
 * comes back here afterwards (AuthRedirect honours the flag below).
 */

export const CONNECT_FLAG = "whgdocs.wordAddin.connect";

// A bare request on purpose: the shared API client answers a 401 by sending
// the whole window to "/" before we can set CONNECT_FLAG, which would lose
// the way back here. We want the 401 itself, so we can show "Sign in". The
// CSRF token still has to be fetched the shared way (on production the API's
// own cookie is rejected cross-host).
const mintToken = async () => {
  const csrfToken = await ensureCsrfToken();
  return axios.post(
    `${process.env.REACT_APP_API_URL}word-addin/token/`,
    {},
    {
      withCredentials: true,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
      },
    }
  );
};

const Wrap = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  font-family: "Manrope", sans-serif;
  background: #f8fafc;
  color: #0f172a;
  padding: 24px;
  text-align: center;
`;

const Card = styled.div`
  max-width: 360px;
  display: grid;
  gap: 12px;
`;

const Button = styled.button`
  border: 0;
  background: #1e40af;
  color: #fff;
  border-radius: 9px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
`;

const WordAddinConnect = () => {
  const [state, setState] = useState("working");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const go = async () => {
      try {
        await loadOffice().catch(() => null);
        const res = await mintToken();
        if (cancelled) return;
        window.sessionStorage.removeItem(CONNECT_FLAG);
        messageParent(JSON.stringify({ type: "tokens", ...res.data }));
        setState("done");
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401 || err?.response?.status === 403 || err?.isAuthRedirect) {
          setState("signin");
        } else {
          setError(err?.response?.data?.error || "WHG Docs did not answer. Try again.");
          setState("error");
        }
      }
    };
    go();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = () => {
    window.sessionStorage.setItem(CONNECT_FLAG, "1");
    window.location.assign("/");
  };

  return (
    <Wrap>
      <Card data-testid="word-connect">
        {state === "working" && <p>Connecting the Word panel to WHG Docs…</p>}
        {state === "done" && <p>Connected. You can close this window.</p>}
        {state === "signin" && (
          <>
            <p>Sign in to WHG Docs to use the template panel in Word.</p>
            <Button type="button" onClick={signIn} data-testid="word-connect-signin">
              Sign in to WHG Docs
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <p style={{ color: "#b91c1c" }}>{error}</p>
            <Button type="button" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </>
        )}
      </Card>
    </Wrap>
  );
};

export default WordAddinConnect;
